import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ClipboardCheck, Compass, LineChart, ShieldCheck, Users } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { SITE_URL, COMPANY } from "../lib/seo";
import { OpisAccessModal } from "../components/OpisAccessModal";

const PILLARS = [
    { name: "Leadership", note: "Governance, vision, decision making cadence, ownership alignment." },
    { name: "People", note: "Team structure, capability, retention, head of department strength, culture." },
    { name: "Operations", note: "SOP maturity, service delivery, back of house discipline, standards." },
    { name: "Guest Experience", note: "Journey mapping, quality audits, feedback loops, guest scores." },
    { name: "Commercial Performance", note: "Revenue mix, pricing, distribution, forecasting rigour." },
    { name: "Financial Performance", note: "Cost lines, margin discipline, cash management, reporting cadence." },
];

const BENEFITS = [
    {
        icon: Compass,
        title: "A single, structured view of operational health",
        body: "OPIS™ replaces scattered anecdote with a structured executive view of the property across six pillars, based on the information you supply.",
    },
    {
        icon: LineChart,
        title: "Clarity on where to act next",
        body: "Every score is followed by interpretation, business implications and a clear recommendation. Priorities are ordered, not listed.",
    },
    {
        icon: ClipboardCheck,
        title: "A 90 day plan that leadership can hold",
        body: "Recommendations become tracked actions with owners, due dates and progress. The plan is auditable at the next review.",
    },
    {
        icon: Users,
        title: "Alignment across ownership and operations",
        body: "One report, one language, one set of priorities that owners, general managers and operations leaders can act on together.",
    },
    {
        icon: ShieldCheck,
        title: "Reviewed personally by a hospitality operator",
        body: "Every report is reviewed by Sidney Adams before release. The findings are yours, not a template.",
    },
];

export const OPIS_PORTAL_URL = "https://opis.optimumhospitalitysolutions.com";
export const OPIS_PORTAL_LOGIN_URL = `${OPIS_PORTAL_URL}/login`;

export default function OpisPage() {
    const [accessOpen, setAccessOpen] = useState(false);

    const openApply = () => setAccessOpen(true);

    useSEO({
        title: "OPIS™ — Executive Decision Intelligence for Hotels | Optimum Hospitality Solutions",
        description:
            "OPIS™ is the Executive Decision Intelligence Platform from Optimum Hospitality Solutions. Structured executive reviews across six operational pillars, professionally reviewed, translated into a 90 day plan.",
        path: "/opis",
    });

    const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "OPIS™ Executive Decision Intelligence Platform",
        brand: { "@type": "Brand", name: "Optimum Hospitality Solutions" },
        description:
            "Executive Decision Intelligence Platform for hotels. Structured business health reviews across six operational pillars, professionally reviewed by a hospitality operator, translated into a 90 day executive action plan.",
        manufacturer: {
            "@type": "Organization",
            name: COMPANY.legalName,
            url: SITE_URL,
        },
        audience: {
            "@type": "BusinessAudience",
            audienceType: "Hotel owners, managing directors, general managers and operations leaders",
        },
    };

    return (
        <div data-testid="opis-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />

            {/* Hero */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-8">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Introducing OPIS<sup className="text-[8px] ml-0.5">™</sup>
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                            Executive Decision Intelligence for hotels.
                        </h1>
                        <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                            OPIS<sup className="text-[10px]">™</sup> is a structured executive review of your property across six operational pillars. Reviewed personally by a hospitality operator. Translated into a clear 90 day plan the leadership team can hold.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-brand-ink/70 max-w-2xl">
                            Built by Optimum Hospitality Solutions on more than a decade of in property operating experience across independent hotels, aparthotels, restaurants and multi site groups.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <button
                                type="button"
                                onClick={openApply}
                                data-testid="opis-hero-apply"
                                className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                            >
                                Apply for Founding Member Access <ArrowUpRight size={16} />
                            </button>
                            <a
                                href={OPIS_PORTAL_LOGIN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="opis-hero-login"
                                className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-navy text-brand-navy text-sm tracking-wide hover:bg-brand-navy hover:text-brand-stone transition-colors"
                            >
                                OPIS<sup className="text-[8px]">™</sup> Login <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="border border-brand-border bg-brand-stone p-8">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Founding Member Programme
                            </div>
                            <p className="mt-4 text-sm text-brand-ink/80 leading-relaxed">
                                Founding Member applications are now open for a limited number of properties. Each application is reviewed personally by Sidney Adams. Founding Member access is currently offered free of charge during the introductory phase, subject to application and approval.
                            </p>
                            <Link
                                to="/opis/founding-members"
                                data-testid="opis-hero-founding-link"
                                className="mt-5 inline-flex items-center gap-2 text-sm text-brand-navy link-underline"
                            >
                                About the programme <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why OPIS */}
            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Why OPIS<sup className="text-[8px] ml-0.5">™</sup></span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy">
                            Reporting is not the problem.<br />Decisions are.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5 text-brand-ink/80 leading-relaxed">
                        <p>
                            Most hotel businesses do not lack data. They have a property management system, a finance system, a payroll system and a guest satisfaction platform. What they lack is a structured, executive view that translates the numbers into the two or three decisions that will move the property this quarter.
                        </p>
                        <p>
                            OPIS™ closes that gap. Not another dashboard. Not another audit. A structured business health review across the six pillars that determine whether a hotel operates well or drifts, followed by a professionally reviewed executive report and a 90 day plan that leadership can hold in front of ownership, lenders and the board.
                        </p>
                        <p>
                            Every score is followed by four questions the platform answers on your behalf. What does this mean? Why does it matter? What are the business implications? What should leadership do next?
                        </p>
                    </div>
                </div>
            </section>

            {/* How OPIS Works */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">How OPIS<sup className="text-[8px] ml-0.5">™</sup> works</span>
                    <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy max-w-2xl">
                        A structured executive review, from beginning to plan.
                    </h2>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border">
                        {[
                            {
                                step: "01",
                                title: "Property profile",
                                body: "A one time comprehensive profile of the property. Identity, location, scale, leadership, cadence. The foundation of the review.",
                            },
                            {
                                step: "02",
                                title: "Executive Business Health Review",
                                body: "A structured review across six pillars. Save and resume, evidence attachments, one pillar at a time. Typically 60 minutes.",
                            },
                            {
                                step: "03",
                                title: "Professional review",
                                body: "Every submission is reviewed personally by Sidney Adams. Findings are interpreted, recommendations are property specific.",
                            },
                            {
                                step: "04",
                                title: "Executive Report and 90 day plan",
                                body: "A branded executive report suitable for owners, boards, lenders and investors. Recommendations become tracked actions.",
                            },
                        ].map((s) => (
                            <div key={s.step} className="bg-brand-stone p-8 lg:p-10">
                                <div className="font-display text-3xl text-brand-champagne">{s.step}</div>
                                <h3 className="font-display text-xl text-brand-navy mt-4 leading-snug">{s.title}</h3>
                                <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 text-sm text-brand-ink/70 max-w-3xl leading-relaxed">
                        Every completed review adds to the property&rsquo;s performance history. OPIS™ demonstrates progress across successive reviews rather than serving as a one off diagnostic.
                    </p>
                </div>
            </section>

            {/* Six Pillars */}
            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">The six pillars</span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy">
                            The six areas that determine operational health.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed">
                            Each pillar is examined against a structured set of questions with guidance, evidence attachments and space for context. Every response is transparent. Every score is defensible.
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <ol className="space-y-4">
                            {PILLARS.map((p, i) => (
                                <li key={p.name} className="grid grid-cols-12 gap-4 border-b border-brand-border pb-4 last:border-0">
                                    <span className="col-span-1 font-display text-brand-champagne">{String(i + 1).padStart(2, "0")}</span>
                                    <div className="col-span-11">
                                        <div className="font-display text-lg text-brand-navy">{p.name}</div>
                                        <p className="mt-1 text-sm text-brand-ink/70 leading-relaxed">{p.note}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* The Optimum Standard */}
            <section className="border-b border-brand-border bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">The methodology</span>
                            <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-stone">
                                The Optimum Standard<sup className="text-[10px] ml-0.5">™</sup>
                            </h2>
                        </div>
                        <div className="lg:col-span-7 space-y-5 text-brand-stone/80 leading-relaxed">
                            <p>
                                The Optimum Standard™ is the methodology behind every OPIS™ engagement. It is drawn from more than a decade of hospitality operating experience across independent hotels, aparthotels, restaurants and multi site groups, and refined through the consulting work Optimum Hospitality Solutions has led in the property.
                            </p>
                            <p>
                                It is built around three principles. Clarity over data. Every insight becomes an action. Continuous, not one off. Where a traditional operational audit produces a document, The Optimum Standard produces a decision.
                            </p>
                            <p>
                                The methodology is transparent by design. The scoring is published, the question bank is versioned, the interpretation is authored by an experienced operator rather than generated. Every finding can be traced back to the response and the evidence that produced it.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Benefits</span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy">
                            Built for the people who own the outcome.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed">
                            OPIS™ is written for hotel owners, managing directors, general managers and operations leaders. Presentable at board level and actionable at head of department level in the same week.
                        </p>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                        {BENEFITS.map((b) => {
                            const Icon = b.icon;
                            return (
                                <div key={b.title} className="flex gap-5">
                                    <div className="shrink-0 w-11 h-11 bg-brand-champagne/15 flex items-center justify-center">
                                        <Icon className="text-brand-champagne" size={20} strokeWidth={1.25} />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-lg text-brand-navy leading-snug">{b.title}</h3>
                                        <p className="mt-2 text-sm text-brand-ink/75 leading-relaxed">{b.body}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Why trust us */}
            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Who reviews your property</span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy">
                            Reviewed personally.<br />Not by a template.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5 text-brand-ink/80 leading-relaxed">
                        <p>
                            OPIS™ is not a self scored questionnaire. Every submission is reviewed personally by Sidney Adams, founder of Optimum Hospitality Solutions and a hospitality operator with more than a decade of in property experience across independent hotels, aparthotels, restaurants and multi site groups.
                        </p>
                        <p>
                            The interpretation, the recommendations and the 90 day plan are written for your property, using the evidence you have supplied. Nothing is generated. Nothing is templated. The name on the report is a real operator, accountable for what it says.
                        </p>
                        <p>
                            This is why OPIS™ is invitation only during the Founding Member phase. It is the only way to ensure that every report meets the standard we would sign our name to.
                        </p>
                        <Link
                            to="/about"
                            data-testid="opis-about-link"
                            className="inline-flex items-center gap-2 text-sm text-brand-navy link-underline"
                        >
                            About Sidney Adams and Optimum Hospitality Solutions <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Founding Member teaser */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Founding Member Programme</span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy">
                            Invitation only. Reviewed personally. Limited cohort.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed max-w-2xl">
                            Founding Member applications are now open for a limited number of properties. Each application is reviewed personally by Sidney Adams. The emphasis is on quality of fit rather than volume. If accepted, your property becomes part of a small cohort shaping the direction of OPIS™ during its introductory phase, with Founding Member access currently offered free of charge subject to approval.
                        </p>
                    </div>
                    <div className="lg:col-span-5 space-y-3 lg:text-right">
                        <div>
                            <button
                                type="button"
                                onClick={openApply}
                                data-testid="opis-bottom-apply"
                                className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover"
                            >
                                Apply for Founding Member Access <ArrowUpRight size={16} />
                            </button>
                        </div>
                        <div>
                            <Link
                                to="/opis/founding-members"
                                data-testid="opis-bottom-founding-link"
                                className="inline-flex items-center gap-2 text-sm text-brand-navy link-underline"
                            >
                                About the Founding Member Programme <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-stone">
                            Would you like to see whether your property is a fit?
                        </h2>
                        <p className="mt-5 text-brand-stone/75 max-w-2xl text-lg">
                            Book a confidential 30 minute discovery call with Sidney Adams. We will discuss your property, whether OPIS™ is the right fit, and next steps if it is.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm text-brand-stone/70">
                            <li className="flex items-center gap-2"><Check size={14} className="text-brand-champagne" /> Complimentary, no obligation</li>
                            <li className="flex items-center gap-2"><Check size={14} className="text-brand-champagne" /> Confidential to your property and ownership</li>
                            <li className="flex items-center gap-2"><Check size={14} className="text-brand-champagne" /> Held by Sidney Adams personally</li>
                        </ul>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link
                            to="/booking?type=discovery&topic=opis"
                            data-testid="opis-cta-book"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white"
                        >
                            Book an OPIS™ discovery call <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            <OpisAccessModal open={accessOpen} onOpenChange={setAccessOpen} />
        </div>
    );
}
